import * as Yup from 'yup'
import { DefaultFormValue, DeliveryMethodTypes, DistributionReasonTypes, RecipientTypes } from './Types'
import { some, find, values, every, map, mapValues } from 'lodash'
import { CountryItem } from '../../reducers/types'
import {
  RetirementConstraintsResponse,
  TaxWithholdingTypes,
  DistributionReasonInfo,
  DistributionReasonEnum
} from '@Types'
import { isValidWithholdingType } from '../../components/IraDistributionForm'
import {
  TransferFrequencyType,
  TransferRecurrenceType,
  WhenToSendUnion
} from '../../components/TransferFrequency/Types'
import moment from 'moment'

const isAddressRequired = (sendTo: RecipientTypes) => {
  return sendTo === 'ALTERNATE-ADDRESS' || sendTo === 'THIRD-PARTY'
}

const isDateValid = (dateValue: string | Date) => {
  const formattedDate = new Date(dateValue).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  return !isNaN(new Date(dateValue).getTime()) && moment(formattedDate, 'MM/DD/YYYY', true).isValid()
}

export const checkTransferSchema = (
  countryList: CountryItem[],
  defaultValues: DefaultFormValue,
  iraConstraints: RetirementConstraintsResponse,
  selectedReason?: DistributionReasonInfo | null
) =>
  Yup.object({
    fromAccount: Yup.string().required('Required').default(defaultValues.fromAccount),
    totalBalanceCheckbox: Yup.boolean().required().default(false),
    transferAmountRaw: Yup.number()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.number()
          .nullable()
          .required('Required')
          .typeError('')
          .min(0.01, 'Amount must be greater than $0.00')
          .default(0)
      })
      .default(0),
    transferAmountDisplay: Yup.string()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.string().required('Required')
      })
      .default(''),
    sendTo: Yup.mixed<RecipientTypes>()
      .oneOf(['ACCOUNT-HOLDER', 'ALTERNATE-ADDRESS', 'THIRD-PARTY'])
      .default('ACCOUNT-HOLDER'),
    beneficiaryName: Yup.string()
      .when('sendTo', {
        is: 'THIRD-PARTY',
        then: Yup.string()
          .required('Required')
          .max(32, 'Maximum 32 characters')
          .matches(/^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    recipientAddress: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string().required('Required').max(100, 'Maximum 100 characters')
      })
      .default(''),
    recipientAddressLine2: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string().max(100, 'Maximum 100 characters')
      })
      .default(''),
    recipientCity: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string()
          .required('Required')
          .max(50, 'Maximum 50 characters')
          .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    recipientState: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string()
          .max(50, 'Maximum 50 characters')
          .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    recipientZip: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string()
          .max(25, 'Maximum 25 characters')
          .matches(/^$|^[0-9a-zA-Z &/#(),.-]+$/, 'Invalid character')
      })
      .default(''),
    recipientCountry: Yup.string()
      .when('sendTo', {
        is: (sendTo: RecipientTypes) => isAddressRequired(sendTo),
        then: Yup.string()
          .required('Required')
          .test('is-invalid', 'Invalid Country', (value) => some(countryList, { isoName: value }))
          .test('is-restricted', 'Blocked Jurisdiction', (value) => {
            const country = find(countryList, { isoName: value })
            const restrictions = country?.restrictions
            return every(values(restrictions), (item) => !item)
          })
      })
      .default(''),
    deliveryMethod: Yup.mixed<DeliveryMethodTypes>()
      .test('delivery-method', 'Required', (value) => {
        if (value) {
          return ['STANDARD', 'OVERNIGHT', 'OVERNIGHT_TO_APEX'].includes(value)
        }
        return false
      })
      .default(''),
    memo1: Yup.string().max(30, '').default(''),
    memo2: Yup.string().max(30, '').default(''),
    memo3: Yup.string().max(30, '').default(''),
    distributionReason: Yup.lazy(() => {
      if (defaultValues.isIra) {
        return Yup.mixed<DistributionReasonEnum | ''>()
          .test('distribution-reason', 'Required', (value) => {
            if (value) {
              return map(DistributionReasonEnum).includes(value)
            }
            return false
          })
          .default('')
      }
      return Yup.mixed<DistributionReasonTypes>().default('')
    }),
    federalTaxWithholdingType: Yup.mixed<TaxWithholdingTypes>().default(null),
    federalTaxWithholdingAmountDisplay: Yup.lazy(() => {
      if (defaultValues.isIra && selectedReason?.withholdingEnabled) {
        return Yup.string()
          .when('federalTaxWithholdingType', {
            is: (federalTaxWithholdingType: TaxWithholdingTypes) => isValidWithholdingType(federalTaxWithholdingType),
            then: Yup.string().required('Required')
          })
          .default('')
      }
      return Yup.string().default('')
    }),
    federalTaxWithholdingAmountRaw: Yup.lazy(() => {
      if (defaultValues.isIra && selectedReason?.withholdingEnabled) {
        return Yup.number()
          .typeError('')
          .when('federalTaxWithholdingType', {
            is: (federalTaxWithholdingType: TaxWithholdingTypes) => isValidWithholdingType(federalTaxWithholdingType),
            then: Yup.number()
              .required('Required')
              .typeError('')
              .test('total-percentage-withholding', ' ', (value, context) => {
                const {
                  stateTaxWithholdingAmountRaw,
                  federalTaxWithholdingType,
                  stateTaxWithholdingType
                } = context.parent
                const bothPercentageWithholdingsSelected =
                  !!value &&
                  !!stateTaxWithholdingAmountRaw &&
                  stateTaxWithholdingType === 'PERCENTAGE' &&
                  federalTaxWithholdingType === 'PERCENTAGE'
                const onlyFedPercentageEnabled = !!value && federalTaxWithholdingType === 'PERCENTAGE'
                if (bothPercentageWithholdingsSelected) {
                  return value + stateTaxWithholdingAmountRaw <= 100
                }
                if (onlyFedPercentageEnabled) {
                  return !!value && value <= 100
                }
                return true
              })
          })
          .default(0)
      }
      return Yup.number().default(0)
    }),
    stateTaxWithholdingType: Yup.mixed<TaxWithholdingTypes>().default(null),
    stateTaxWithholdingAmountDisplay: Yup.lazy(() => {
      if (defaultValues.isIra && selectedReason?.stateWithholdingEnabled) {
        return Yup.string()
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) => isValidWithholdingType(stateTaxWithholdingType),
            then: Yup.string().required('Required')
          })
          .default('')
      }
      return Yup.string().default('')
    }),
    stateTaxWithholdingAmountRaw: Yup.number()
      .when('stateTaxWithholdingType', {
        is: (type: string) => type && type.length > 0,
        then: Yup.number()
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) => isValidWithholdingType(stateTaxWithholdingType),
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than $0.00')
          })
          .when('stateTaxWithholdingType', {
            is: (stateTaxWithholdingType: TaxWithholdingTypes) => stateTaxWithholdingType === 'PERCENTAGE',
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than 0%')
              .max(100, 'Amount must be lower than 100%')
          })
      })
      .default(0),
    receivingInstitutionName: Yup.string()
      .when('distributionReason', {
        is: (value: string) => value === 'TRANSFER',
        then: Yup.string().required('Required')
      })
      .default(''),
    ...(!!iraConstraints.contributionConstraints && {
      contributionType: Yup.string()
        .when('toAccount', {
          is: (val: string) => val && val.length > 0 && val === defaultValues.accountNumber,
          then: Yup.string().required('Required')
        })
        .default(''),

      ...(!iraConstraints.contributionConstraints.onlyAllowCurrentYearContribution && {
        contributionYear: Yup.string()
          .when(['contributionType', 'toAccount'], {
            is: (contributionType: string, toAccount: string) => {
              const isDeposit = toAccount === defaultValues.accountNumber
              const doesSelectedTypeAllowPrevious = iraConstraints.contributionConstraints?.validContributions.find(
                (item) => item.reason === contributionType
              )?.previousYearContributionAllowed
              return (
                isDeposit &&
                !iraConstraints.contributionConstraints?.onlyAllowCurrentYearContribution &&
                !!doesSelectedTypeAllowPrevious
              )
            },
            then: Yup.string().required()
          })
          .default('')
      })
    }),
    transferRecurrence: Yup.mixed<TransferRecurrenceType>().default('ONE_TIME'),
    whenToSend: Yup.mixed<WhenToSendUnion>().default('IMMEDIATELY'),
    transferScheduledDate: Yup.string()
      .when('whenToSend', {
        is: 'IN_THE_FUTURE',
        then: Yup.string()
          .test({
            message: () => '',
            test: function (value, context) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    transferFrequency: Yup.mixed<TransferFrequencyType>()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.mixed<TransferFrequencyType>().required('Required').default('WEEKLY')
      })
      .default('WEEKLY'),
    transferStartDate: Yup.string()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.string()
          .test({
            message: () => 'Invalid date',
            test: function (value) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .test({
            message: () => 'Recurring transfers not allowed for total balance transfers',
            test: function (_, context) {
              return !context.parent.totalBalanceCheckbox
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    numberOfTransfers: Yup.number()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.number().max(100, 'Maximum 100').min(1, 'Value must be greater than 0').default(undefined)
      })
      .default(undefined),
    hasTotalDistributionError: Yup.boolean().when('totalBalanceCheckbox', {
      is: true,
      then: Yup.boolean().test({
        message: () => 'This type of IRA account does not support a total amount transfer.',
        test: () => {
          if (!!defaultValues.isIra) {
            return !!iraConstraints?.distributionConstraints?.totalDistributionAllowed
          }
          return true
        }
      })
    }),
    fees: Yup.lazy((obj) =>
      Yup.object(
        mapValues(obj, () =>
          Yup.object({
            chargeCustomer: Yup.boolean().default(false),
            feeTotal: Yup.number().default(0)
          })
        )
      ).default({})
    )
  })
